import { isDefined } from "@clipboard-health/util-ts";
import { CssBaseline, List, ThemeProvider } from "@mui/material";
import {
  ListItem,
  ListItemIconSkeleton,
  ListItemTextSkeleton,
  Text,
} from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { useState } from "react";

import { PageWrapper } from "../redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "../redesign/components/StickyScrollPageHeader";
import { useDefinedWorker } from "../Worker/useDefinedWorker";
import { useGetWorkerBadges } from "./api/useGetWorkerBadges";
import { type BadgeType } from "./constants";
import { WorkerBadgeModal } from "./Modal";
import { WorkerBadgeListItem } from "./WorkerBadgeListItem";

export function WorkerBadgesPage() {
  const { userId: workerId } = useDefinedWorker();
  const { data: badges, isLoading } = useGetWorkerBadges(workerId);

  const [selectedBadge, setSelectedBadge] = useState<BadgeType>();

  const sortedBadges = badges?.data.sort((a, b) => {
    const aDate = new Date(a.attributes.createdAt);
    const bDate = new Date(b.attributes.createdAt);

    return bDate.getTime() - aDate.getTime();
  });

  return (
    <ThemeProvider theme={getTheme()}>
      <CssBaseline />
      <PageWrapper>
        <StickyScrollPageHeader title="Work Badge" />

        <Text
          variant="body1"
          sx={(theme) => ({ color: theme.palette.text.secondary, paddingX: 7 })}
        >
          These badges are awarded to you based on your performance and contributions. Thank you for
          being a valuable member of the platform!
        </Text>

        <List sx={{ px: 7, paddingBottom: 7 }}>
          {isLoading &&
            Array.from({ length: 3 }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <ListItem key={index}>
                <ListItemIconSkeleton />
                <ListItemTextSkeleton />
              </ListItem>
            ))}

          {!isLoading &&
            sortedBadges?.map((badge) => (
              <WorkerBadgeListItem
                key={badge.id}
                badgeName={badge.attributes.name}
                onClick={() => {
                  setSelectedBadge(badge.attributes.name as BadgeType);
                }}
              />
            ))}
        </List>
      </PageWrapper>

      {isDefined(selectedBadge) && (
        <WorkerBadgeModal
          badgeType={selectedBadge}
          onClose={() => {
            setSelectedBadge(undefined);
          }}
        />
      )}
    </ThemeProvider>
  );
}
